import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toDisplayFileUrl } from './fileUrls';

/** Where the video buttons go among the links; see publicationVideos. */
const VIDEOS = 'videos';

/**
 * The resource links a Publication can carry, in the order they are shown.
 *
 * Each entry names its schema field; the VIDEOS entry stands for all of the
 * item's videos.
 * `isFile` marks the ones that point at an uploaded file rather than an
 * external URL, so they go through @@display-file.
 */
export const PUBLICATION_LINK_FIELDS = [
  {
    key: 'publication_pdf_file',
    label: 'PDF',
    isFile: true,
  },
  {
    key: 'publication_pre_print_link',
    label: 'Preprint',
  },
  {
    key: 'publication_publication_link',
    label: 'Publication',
  },
  {
    key: 'publication_paper_website',
    label: 'Paper Website',
  },
  {
    key: 'publication_code_repository',
    label: 'Code',
  },
  {
    key: 'publication_dataset_link',
    label: 'Dataset',
  },
  { key: VIDEOS },
  { key: 'publication_file_link', label: 'File' },
  {
    key: 'publication_video_file',
    label: 'Video File',
    isFile: true,
  },
];

const videoPath = (url) =>
  flattenToAppURL(url || '')
    .split('?')[0]
    .replace(/\/index\.html$/, '')
    .replace(/\/$/, '');

/**
 * Every video of a publication: its Video items (related_videos) and the
 * Video Link, which holds an external video or a video file.
 *
 * A listing result carries both merged in the `video_links` column; a full
 * object carries the fields themselves. A link that only repeats a related
 * video's path is shown once.
 */
export const publicationVideos = (item) => {
  let videos;
  if (Array.isArray(item.video_links)) {
    videos = item.video_links.map((v) => ({ href: v.url, title: v.title }));
  } else {
    videos = (item.related_videos || [])
      .filter((v) => v?.['@id'])
      .map((v) => ({ href: v['@id'], title: v.title }));
    const link = item.publication_video_link;
    const linkPath = videoPath(link);
    // A file inside a related video's folder is that video again.
    const repeatsVideo = videos.some(
      (v) =>
        linkPath === videoPath(v.href) ||
        linkPath.startsWith(`${videoPath(v.href)}/`),
    );
    if (link && !repeatsVideo) {
      videos.push({ href: link, title: null });
    }
  }
  return videos.map((v, index) => ({
    key: `video-${index}`,
    label: videos.length > 1 ? `Video ${index + 1}` : 'Video',
    title: v.title || undefined,
    href: flattenToAppURL(v.href),
  }));
};

/** The links this item actually has a value for, with their href. */
export const activePublicationLinks = (item) =>
  PUBLICATION_LINK_FIELDS.flatMap((f) => {
    if (f.key === VIDEOS) return publicationVideos(item);
    const value = item[f.key];
    if (!value) return [];
    return [
      {
        ...f,
        href: f.isFile ? toDisplayFileUrl(value) : flattenToAppURL(value),
      },
    ];
  });
